import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Chart from "react-apexcharts";
import { FaTimes, FaSyncAlt, FaChartLine } from "react-icons/fa";
import { request } from "../../services/api";

const getCurrentThemeKey = () => {
  if (typeof document === "undefined") return "default";
  return document.documentElement.getAttribute("data-sfarm-theme") || "default";
};

const ARCHIVE_THEME_PALETTES = {
  default: {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.65)",
    modalBg:
      "linear-gradient(145deg, rgba(22, 36, 71, 0.98), rgba(15, 23, 42, 0.98))",
    headerBg: "rgba(15, 23, 42, 0.92)",
    toolbarBg: "rgba(0, 0, 0, 0.08)",
    color: "#fff5e6",
    muted: "rgba(255, 245, 230, 0.68)",
    border: "rgba(255, 245, 230, 0.12)",
    inputBg: "rgba(0, 0, 0, 0.24)",
    inputColor: "#ffffff",
    primary: "#73c2fb",
    primaryColor: "#162447",
    chartText: "#bdc3c7",
    grid: "rgba(255, 255, 255, 0.12)",
    icon: "#f1c40f",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.58)",
  },
  "smart-home": {
    isLight: false,
    overlay: "rgba(0, 0, 0, 0.68)",
    modalBg:
      "linear-gradient(145deg, rgba(12, 23, 48, 0.98), rgba(5, 12, 28, 0.98))",
    headerBg: "rgba(6, 17, 40, 0.92)",
    toolbarBg: "rgba(126, 232, 255, 0.05)",
    color: "#e9fbff",
    muted: "rgba(233, 251, 255, 0.68)",
    border: "rgba(126, 232, 255, 0.18)",
    inputBg: "rgba(0, 0, 0, 0.28)",
    inputColor: "#ffffff",
    primary: "#4de1ff",
    primaryColor: "#061128",
    chartText: "#c9f6ff",
    grid: "rgba(126, 232, 255, 0.14)",
    icon: "#4de1ff",
    shadow: "0 28px 90px rgba(0, 0, 0, 0.6)",
  },
  "industrial-blue": {
    isLight: true,
    overlay: "rgba(18, 38, 58, 0.38)",
    modalBg:
      "linear-gradient(145deg, rgba(229, 243, 251, 0.97), rgba(207, 230, 244, 0.96))",
    headerBg: "rgba(245, 250, 253, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.28)",
    color: "#18344f",
    muted: "rgba(24, 52, 79, 0.68)",
    border: "rgba(24, 52, 79, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.86)",
    inputColor: "#18344f",
    primary: "#2f83c7",
    primaryColor: "#ffffff",
    chartText: "#28455f",
    grid: "rgba(24, 52, 79, 0.14)",
    icon: "#2f83c7",
    shadow: "0 28px 90px rgba(24, 52, 79, 0.24)",
  },
  "home-soft": {
    isLight: true,
    overlay: "rgba(31, 45, 58, 0.34)",
    modalBg:
      "linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(240, 247, 250, 0.94))",
    headerBg: "rgba(255, 255, 255, 0.58)",
    toolbarBg: "rgba(255, 255, 255, 0.34)",
    color: "#26384c",
    muted: "rgba(38, 56, 76, 0.68)",
    border: "rgba(52, 73, 94, 0.16)",
    inputBg: "rgba(255, 255, 255, 0.9)",
    inputColor: "#26384c",
    primary: "#6a9c89",
    primaryColor: "#ffffff",
    chartText: "#34495e",
    grid: "rgba(52, 73, 94, 0.14)",
    icon: "#6a9c89",
    shadow: "0 28px 90px rgba(31, 45, 58, 0.22)",
  },
};

const useArchiveTheme = (isOpen) => {
  const [themeKey, setThemeKey] = useState(getCurrentThemeKey);

  useEffect(() => {
    if (!isOpen) return undefined;

    const syncTheme = () => setThemeKey(getCurrentThemeKey());
    syncTheme();

    window.addEventListener("sfarm-theme-change", syncTheme);
    window.addEventListener("storage", syncTheme);

    return () => {
      window.removeEventListener("sfarm-theme-change", syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, [isOpen]);

  return ARCHIVE_THEME_PALETTES[themeKey] || ARCHIVE_THEME_PALETTES.default;
};

const useViewportSize = (isOpen) => {
  const getSize = () => {
    if (typeof window === "undefined") {
      return { width: 1024, height: 768 };
    }

    return {
      width: window.innerWidth || 1024,
      height: window.innerHeight || 768,
    };
  };

  const [viewportSize, setViewportSize] = useState(getSize);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return undefined;

    const syncViewportSize = () => setViewportSize(getSize());
    syncViewportSize();

    window.addEventListener("resize", syncViewportSize);
    window.addEventListener("orientationchange", syncViewportSize);

    return () => {
      window.removeEventListener("resize", syncViewportSize);
      window.removeEventListener("orientationchange", syncViewportSize);
    };
  }, [isOpen]);

  return viewportSize;
};

const MOBILE_CHART_DESIGN_WIDTH = 1000;
const MOBILE_CHART_DESIGN_HEIGHT = 420;
const MOBILE_CHART_MIN_SCALE = 0.34;
const MOBILE_MODAL_SAFE_GAP = 16;

const buildArchiveStyles = (theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    background: theme.overlay,
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backdropFilter: "blur(6px)",
  },
  modal: {
    width: "min(1000px, 95vw)",
    maxHeight: "90vh",
    background: theme.modalBg,
    color: theme.color,
    borderRadius: "18px",
    boxShadow: theme.shadow,
    overflow: "hidden",
    border: `1px solid ${theme.border}`,
  },
  header: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 48px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.headerBg,
    direction: "rtl",
    textAlign: "center",
  },
  iconButton: {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    border: `1px solid ${theme.border}`,
    background: theme.isLight
      ? "rgba(255,255,255,0.72)"
      : "rgba(255,255,255,0.06)",
    color: theme.color,
    cursor: "pointer",
    fontSize: "16px",
    width: "34px",
    height: "34px",
    borderRadius: "11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.border}`,
    background: theme.toolbarBg,
    direction: "rtl",
  },
  toolbarItem: {
    flex: "1 1 0",
    display: "flex",
  },
  filterItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    direction: "rtl",
    color: theme.color,
  },
  select: {
    background: theme.inputBg,
    color: theme.inputColor,
    border: `1px solid ${theme.border}`,
    borderRadius: "8px",
    padding: "6px 8px",
    outline: "none",
  },
  refreshButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: theme.primary,
    color: theme.primaryColor,
    border: "none",
    borderRadius: "8px",
    padding: "7px 12px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 800,
  },
  info: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "10px 16px",
    fontSize: "12px",
    color: theme.muted,
    direction: "rtl",
    textAlign: "right",
    borderBottom: `1px solid ${theme.border}`,
  },
  infoItem: {
    flex: "1 1 0",
  },
  error: {
    margin: "10px 16px 0",
    padding: "10px",
    background: "rgba(231, 76, 60, 0.15)",
    border: "1px solid rgba(231, 76, 60, 0.4)",
    color: theme.isLight ? "#9f2d22" : "#ffb3b3",
    borderRadius: "8px",
    fontSize: "13px",
  },
  chartWrapper: {
    padding: "16px",
    direction: "ltr",
  },
});

const ArchiveChartModal = ({
  isOpen,
  onClose,
  serial,
  sensorKey,
  title,
  unit,
}) => {
  const [range, setRange] = useState("-24h");
  const [limit, setLimit] = useState(1000);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const archiveTheme = useArchiveTheme(isOpen);
  const archiveStyles = useMemo(
    () => buildArchiveStyles(archiveTheme),
    [archiveTheme],
  );
  const { width: viewportWidth } = useViewportSize(isOpen);
  const isMobile = viewportWidth <= 768;

  /*
   * Mobile chart policy:
   * Render ApexCharts on a desktop-size virtual canvas, then scale the whole
   * canvas down with CSS. This preserves the desktop proportions, toolbar
   * placement, axis labels, and tick density instead of letting ApexCharts
   * recompute a compressed mobile layout.
   */
  const mobileAvailableWidth = Math.max(
    280,
    viewportWidth - MOBILE_MODAL_SAFE_GAP,
  );
  const mobileChartScale = isMobile
    ? Math.min(
        1,
        Math.max(
          MOBILE_CHART_MIN_SCALE,
          mobileAvailableWidth / MOBILE_CHART_DESIGN_WIDTH,
        ),
      )
    : 1;
  const chartCanvasWidth = isMobile ? MOBILE_CHART_DESIGN_WIDTH : "100%";
  const chartCanvasHeight = isMobile ? MOBILE_CHART_DESIGN_HEIGHT : 420;
  const chartVisibleHeight = Math.ceil(chartCanvasHeight * mobileChartScale);
  const chartHeight = chartCanvasHeight;

  const displaySensorName = title || sensorKey;
  const normalizedSensorKey = String(sensorKey || "").toLowerCase();

  const isDigitalArchive =
    normalizedSensorKey.startsWith("din") ||
    normalizedSensorKey.startsWith("dout");

  const supportsSetpointLine =
    normalizedSensorKey.startsWith("temp") ||
    normalizedSensorKey.startsWith("humi") ||
    normalizedSensorKey.startsWith("wind");

  const toNumberOrNull = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const getRowSetpoint = (row) => {
    return (
      row.setpoint ??
      row.setpoint_snapshot ??
      row.threshold ??
      row.limit ??
      null
    );
  };

  const loadArchive = async () => {
    if (!serial || !sensorKey) {
      setError(
        `اطلاعات سنسور کامل نیست. serial=${serial || "خالی"} sensor=${sensorKey || "خالی"}`,
      );
      setRecords([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const json = await request(
        `/get_sensor_archive.php?serial=${encodeURIComponent(serial)}&sensor=${encodeURIComponent(sensorKey)}&range=${encodeURIComponent(range)}&limit=${encodeURIComponent(limit)}`,
      );

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت داده نمودار");
      }

      const sorted = [...(json.records || [])]
        .filter(
          (row) => row.time && row.value !== null && row.value !== undefined,
        )
        .sort(
          (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
        );

      setRecords(sorted);
    } catch (err) {
      console.error("Archive chart error:", err);
      setError(err.message || "خطای نامشخص");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadArchive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serial, sensorKey, range, limit]);

  if (!isOpen) return null;

  const chartRows = records
    .map((row) => {
      const x = new Date(row.time).getTime();
      const value = toNumberOrNull(row.value);
      const setpoint = toNumberOrNull(getRowSetpoint(row));

      return {
        x,
        value,
        setpoint,
      };
    })
    .filter((point) => !Number.isNaN(point.x) && point.value !== null);

  const chartData = chartRows.map((point) => ({
    x: point.x,
    y: point.value,
  }));

  const setpointData = supportsSetpointLine
    ? chartRows
        .filter((point) => point.setpoint !== null)
        .map((point) => ({
          x: point.x,
          y: point.setpoint,
        }))
    : [];

  const hasSetpointLine = setpointData.length > 0;

  const series = [
    {
      name: displaySensorName,
      type: isDigitalArchive ? "line" : "area",
      data: chartData,
    },
    ...(hasSetpointLine
      ? [
          {
            name: unit ? `حد آستانه (${unit})` : "حد آستانه",
            type: "line",
            data: setpointData,
          },
        ]
      : []),
  ];

  const isLongRange = ["-7d", "-30d", "-90d"].includes(range);

  const formatPersianDateTime = (timestamp) => {
    try {
      return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(timestamp));
    } catch {
      return "";
    }
  };

  const formatPersianTime = (timestamp) => {
    try {
      return new Intl.DateTimeFormat("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(timestamp));
    } catch {
      return "";
    }
  };

  const formatPersianAxisLabel = (timestamp) => {
    if (isLongRange) {
      return formatPersianDateTime(timestamp);
    }

    return formatPersianTime(timestamp);
  };

  const chartType = isDigitalArchive ? "line" : "area";
  const apexChartType = hasSetpointLine ? "line" : chartType;
  const axisTitleColor = archiveTheme.isLight ? archiveTheme.color : "#f5f7fb";
  const axisLabelColor = archiveTheme.chartText;
  const axisLineColor = archiveTheme.isLight
    ? "rgba(24, 52, 79, 0.34)"
    : "rgba(255, 255, 255, 0.32)";
  const axisTickColor = archiveTheme.isLight
    ? "rgba(24, 52, 79, 0.26)"
    : "rgba(255, 255, 255, 0.26)";
  const chartAccentColor = archiveTheme.primary || "#73c2fb";
  const setpointLineColor = archiveTheme.isLight ? "#e67e22" : "#f1c40f";

  const formatAxisValue = (val) => {
    if (val === null || val === undefined || val === "") return "";

    const numericValue = Number(val);

    if (Number.isNaN(numericValue)) return String(val);
    if (Number.isInteger(numericValue)) return String(numericValue);

    return numericValue.toFixed(1);
  };

  const options = {
    chart: {
      type: apexChartType,
      height: chartHeight,
      background: "transparent",
      foreColor: axisLabelColor,
      fontFamily: "IRANSans, Arial, sans-serif",
      parentHeightOffset: 0,
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      animations: {
        // ApexCharts mask reveal animation can throw
        // "w.dom.elDefs is null" when the chart is mounted inside a portal/modal.
        // Keep the improved visual style, but disable SVG reveal animations.
        enabled: false,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          enabled: false,
        },
      },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      dropShadow: {
        enabled: !isDigitalArchive,
        top: 4,
        left: 0,
        blur: 7,
        opacity: archiveTheme.isLight ? 0.16 : 0.28,
      },
    },

    theme: {
      mode: archiveTheme.isLight ? "light" : "dark",
    },

    colors: hasSetpointLine
      ? [chartAccentColor, setpointLineColor]
      : [chartAccentColor],

    stroke: {
      curve: hasSetpointLine
        ? [isDigitalArchive ? "stepline" : "smooth", "stepline"]
        : isDigitalArchive
          ? "stepline"
          : "smooth",
      width: hasSetpointLine ? [3.2, 2.4] : 3.2,
      dashArray: hasSetpointLine ? [0, 7] : 0,
      lineCap: "round",
    },

    fill: {
      type: hasSetpointLine ? ["gradient", "solid"] : "gradient",
      opacity: hasSetpointLine ? [isDigitalArchive ? 0.12 : 0.34, 1] : 1,
      gradient: {
        shadeIntensity: 0.85,
        opacityFrom: isDigitalArchive ? 0.12 : 0.34,
        opacityTo: 0.04,
        stops: [0, 65, 100],
      },
    },

    markers: {
      size: 0,
      strokeWidth: 0,
      hover: {
        size: 5,
        sizeOffset: 2,
      },
    },

    dataLabels: {
      enabled: false,
    },

    xaxis: {
      type: "datetime",
      title: {
        text: "زمان",
        offsetY: 8,
        style: {
          color: axisTitleColor,
          fontSize: "13px",
          fontWeight: 900,
          fontFamily: "IRANSans, Arial, sans-serif",
        },
      },
      axisBorder: {
        show: true,
        color: axisLineColor,
        height: 2,
      },
      axisTicks: {
        show: true,
        color: axisTickColor,
        height: 6,
      },
      labels: {
        datetimeUTC: false,
        rotate: -35,
        trim: true,
        offsetY: 0,
        style: {
          colors: axisLabelColor,
          fontSize: "11px",
          fontWeight: 800,
          fontFamily: "IRANSans, Arial, sans-serif",
        },
        formatter: function (value, timestamp) {
          return formatPersianAxisLabel(timestamp || value);
        },
      },
      tooltip: {
        enabled: false,
      },
    },

    yaxis: {
      title: {
        text: unit ? `مقدار (${unit})` : "مقدار",
        rotate: -90,
        offsetX: -2,
        style: {
          color: axisTitleColor,
          fontSize: "13px",
          fontWeight: 900,
          fontFamily: "IRANSans, Arial, sans-serif",
        },
      },
      labels: {
        style: {
          colors: axisLabelColor,
          fontSize: "11px",
          fontWeight: 800,
          fontFamily: "IRANSans, Arial, sans-serif",
        },
        formatter: formatAxisValue,
      },
      axisBorder: {
        show: true,
        color: axisLineColor,
      },
      axisTicks: {
        show: true,
        color: axisTickColor,
      },
    },

    tooltip: {
      enabled: true,
      theme: archiveTheme.isLight ? "light" : "dark",
      shared: true,
      intersect: false,
      marker: {
        show: true,
      },
      style: {
        fontSize: "12px",
        fontFamily: "IRANSans, Arial, sans-serif",
      },
      x: {
        formatter: function (value) {
          return formatPersianDateTime(value);
        },
      },
      y: {
        formatter: (val) => `${formatAxisValue(val)}${unit ? ` ${unit}` : ""}`,
        title: {
          formatter: (seriesName) => seriesName,
        },
      },
    },

    grid: {
      show: true,
      borderColor: archiveTheme.grid,
      strokeDashArray: 5,
      padding: {
        top: 12,
        right: 18,
        bottom: 10,
        left: 16,
      },
    },

    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: 800,
      labels: {
        colors: axisLabelColor,
      },
      offsetY: 0,
      markers: {
        width: 10,
        height: 10,
        radius: 999,
      },
    },

    noData: {
      text: loading ? "در حال دریافت..." : "داده‌ای برای نمایش وجود ندارد",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: axisLabelColor,
        fontSize: "14px",
        fontWeight: 800,
        fontFamily: "IRANSans, Arial, sans-serif",
      },
    },
  };

  const overlayStyle = {
    ...archiveStyles.overlay,
    ...(isMobile
      ? {
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          padding: "8px",
          overflowY: "auto",
          overflowX: "hidden",
        }
      : {}),
  };

  const modalStyle = {
    ...archiveStyles.modal,
    ...(isMobile
      ? {
          width: "calc(100vw - 16px)",
          maxWidth: "760px",
          minHeight: "min(560px, calc(100dvh - 16px))",
          maxHeight: "calc(100dvh - 16px)",
          borderRadius: "14px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }
      : {}),
  };

  const chartWrapperStyle = {
    ...archiveStyles.chartWrapper,
    ...(isMobile
      ? {
          flex: "1 1 auto",
          minHeight: 0,
          padding: "12px 8px 22px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
      : {}),
  };

  const toolbarStyle = {
    ...archiveStyles.toolbar,
    ...(isMobile
      ? {
          gap: "10px",
          padding: "10px 8px",
        }
      : {}),
  };

  const infoStyle = {
    ...archiveStyles.info,
    ...(isMobile
      ? {
          gap: "6px",
          padding: "8px",
          fontSize: "11px",
        }
      : {}),
  };

  const chartStageStyle = isMobile
    ? {
        width: "100%",
        height: `${chartVisibleHeight}px`,
        minHeight: `${chartVisibleHeight}px`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflow: "visible",
      }
    : {
        width: "100%",
      };

  const chartScaleStyle = isMobile
    ? {
        width: `${MOBILE_CHART_DESIGN_WIDTH}px`,
        height: `${MOBILE_CHART_DESIGN_HEIGHT}px`,
        flex: "0 0 auto",
        transform: `scale(${mobileChartScale})`,
        transformOrigin: "top center",
      }
    : {
        width: "100%",
      };

  return createPortal(
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={archiveStyles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaChartLine color={archiveTheme.icon} />
            <strong>نمودار آرشیو {displaySensorName}</strong>
          </div>

          <button onClick={onClose} style={archiveStyles.iconButton}>
            <FaTimes />
          </button>
        </div>

        <div style={toolbarStyle}>
          <div
            style={{
              ...archiveStyles.toolbarItem,
              justifyContent: "flex-start",
            }}
          >
            <div style={archiveStyles.filterItem}>
              <span>بازه:</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                style={archiveStyles.select}
              >
                <option value="-1h">۱ ساعت اخیر</option>
                <option value="-6h">۶ ساعت اخیر</option>
                <option value="-12h">۱۲ ساعت اخیر</option>
                <option value="-24h">۲۴ ساعت اخیر</option>
                <option value="-7d">۷ روز اخیر</option>
                <option value="-30d">۳۰ روز اخیر</option>
                <option value="-90d">۹۰ روز اخیر</option>
              </select>
            </div>
          </div>

          <div
            style={{ ...archiveStyles.toolbarItem, justifyContent: "center" }}
          >
            <div style={archiveStyles.filterItem}>
              <span>تعداد:</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                style={archiveStyles.select}
              >
                <option value={200}>۲۰۰</option>
                <option value={500}>۵۰۰</option>
                <option value={1000}>۱۰۰۰</option>
                <option value={2000}>۲۰۰۰</option>
                <option value={5000}>۵۰۰۰</option>
              </select>
            </div>
          </div>

          <div
            style={{ ...archiveStyles.toolbarItem, justifyContent: "flex-end" }}
          >
            <button
              onClick={loadArchive}
              style={archiveStyles.refreshButton}
              disabled={loading}
            >
              <FaSyncAlt />
              {loading ? "در حال دریافت..." : "بروزرسانی"}
            </button>
          </div>
        </div>

        <div style={infoStyle}>
          <span style={{ ...archiveStyles.infoItem, textAlign: "right" }}>
            دستگاه: {serial}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "center" }}>
            سنسور: {displaySensorName}
          </span>
          <span style={{ ...archiveStyles.infoItem, textAlign: "left" }}>
            تعداد رکورد: {records.length}
          </span>
        </div>

        {error && <div style={archiveStyles.error}>{error}</div>}

        <div style={chartWrapperStyle}>
          <div style={chartStageStyle}>
            <div style={chartScaleStyle}>
              <Chart
                key={`${sensorKey}-${range}-${limit}-${archiveTheme.isLight ? "light" : "dark"}-${apexChartType}-${hasSetpointLine ? "with-setpoint" : "value-only"}`}
                options={options}
                series={series}
                type={apexChartType}
                width={chartCanvasWidth}
                height={chartHeight}
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ArchiveChartModal;
